// `useLogin` — TanStack mutation wrapping `signInWithEmailAndPassword`.
//
// Returns a TanStack mutation result so the page reads `.error` /
// `.isPending` / `.isSuccess` with the same idioms used by the
// provisioning flow (LP2). The hook itself does NOT navigate — the
// LoginPage owns the redirect on auth-state-change.
//
// Why a mutation rather than a plain async function
// -------------------------------------------------
// The page wants idempotent state for "in-flight", "errored", "ready
// to retry". A bare async function would force the page to manage
// `useState` for each. The TanStack mutation API is already wired in
// the app's QueryClient and is the same shape `useCompleteProvisioning`
// uses, so the page-level branching looks identical.

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFirebaseAuth } from '@/auth/firebase';

export interface LoginInput {
  email: string;
  password: string;
}

export function useLogin(): UseMutationResult<void, Error, LoginInput> {
  return useMutation<void, Error, LoginInput>({
    mutationFn: async ({ email, password }) => {
      const auth = getFirebaseAuth();
      if (!auth) {
        throw new Error('Firebase auth non configurata');
      }
      // Firebase persists the session via SDK defaults — no manual
      // token plumbing here. The AuthGate's `onAuthStateChanged`
      // listener picks up the new user and flips the gate state to
      // `authenticated`, at which point the LoginPage's local
      // observer also fires and triggers the redirect to "/".
      await signInWithEmailAndPassword(auth, email, password);
    },
  });
}
